import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Stack,
  alpha
} from '@mui/material';
import {
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  BarChart as ChartIcon,
  TrendingUp,
  People,
  Assessment,
  CheckCircle,
  DateRange
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import Snackbar from '@mui/material/Snackbar';
import { baseUrl, config } from '../../common/utils';
import { IonContent, IonPage } from '@ionic/react';

// Generate unique request ID
const requestID = () => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const ExamReport = () => {
  const [exams, setExams] = useState([]);
  const [selectedExams, setSelectedExams] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  
  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [searchUser, setSearchUser] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  // Fetch all exams
  const fetchExams = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        `${baseUrl()}Cbt_Exams/index`, 
        formData, 
        config
      );
      
      if (response.data.status === true) {
        setExams(response.data.data || []);
        showSnackbar('Exams loaded successfully', 'success');
      } else {
        showSnackbar('Failed to fetch exams', 'error');
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch exam report
  const fetchReport = async () => {
    if (selectedExams.length === 0) {
      showSnackbar('Please select at least one exam', 'warning');
      return;
    }

    setReportLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      selectedExams.forEach((examId, index) => {
        formData.append(`exam_ids[${index}]`, examId);
      });

      if (startDate) {
        formData.append("start_date", startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        formData.append("end_date", endDate.toISOString().split('T')[0]);
      }
      if (minScore) {
        formData.append("min_score", parseFloat(minScore));
      }
      if (maxScore) {
        formData.append("max_score", parseFloat(maxScore));
      }
      if (searchUser) {
        formData.append("search_user", searchUser);
      }

      const response = await axios.post(
        `${baseUrl()}Cbt_ExamReports/getExamReport`, 
        formData, 
        config
      );

      if (response.data.status) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);
        setPage(1);
        showSnackbar(`Loaded ${response.data.total_records || 0} records`, 'success');
      } else {
        showSnackbar(response.data.message || 'Failed to load report', 'error');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      showSnackbar(error.response?.data?.message || 'Network error occurred', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = async () => {
    if (selectedExams.length === 0) {
      showSnackbar('Please select at least one exam', 'warning');
      return;
    }

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      selectedExams.forEach((examId, index) => {
        formData.append(`exam_ids[${index}]`, examId);
      });

      if (startDate) {
        formData.append("start_date", startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        formData.append("end_date", endDate.toISOString().split('T')[0]);
      }

      const response = await axios.post(
        `${baseUrl()}Cbt_ExamReports/exportReport`, 
        formData, 
        {
          ...config,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exam_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('Report exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting report:', error);
      showSnackbar('Failed to export report', 'error');
    }
  };

  // View attempt details
  const viewAttemptDetails = async (attemptId) => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("attempt_id", attemptId);

      const response = await axios.post(
        `${baseUrl()}Cbt_ExamAttempts/getUserAttemptDetails`, 
        formData, 
        config
      );

      if (response.data.status) {
        setSelectedAttempt(response.data.data);
        setDetailsDialog(true);
      } else {
        showSnackbar('Failed to fetch attempt details', 'error');
      }
    } catch (error) {
      console.error('Error fetching attempt details:', error);
      showSnackbar('Network error occurred', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setMinScore('');
    setMaxScore('');
    setSearchUser('');
    showSnackbar('Filters cleared', 'info');
  };

  // Calculate summary if not provided by API
  const calculateSummary = (data) => {
    if (!data || data.length === 0) {
      return {
        total_attempts: 0,
        avg_score: 0,
        highest_score: 0,
        lowest_score: 0,
        pass_rate: 0,
        total_users: 0
      };
    }

    const scores = data.map(row => row.score_percent || 0);
    const totalAttempts = data.length;
    const avgScore = totalAttempts > 0 ? scores.reduce((a, b) => a + b, 0) / totalAttempts : 0;
    const userIds = [...new Set(data.map(row => row.user_id))];
    
    const passingCount = scores.filter(score => score >= 60).length;

    return {
      total_attempts: totalAttempts,
      total_users: userIds.length,
      avg_score: Math.round(avgScore * 100) / 100,
      highest_score: Math.max(...scores),
      lowest_score: Math.min(...scores),
      pass_rate: totalAttempts > 0 ? Math.round((passingCount / totalAttempts) * 100 * 100) / 100 : 0
    };
  };

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    if (reportData.length > 0 && !summary) {
      setSummary(calculateSummary(reportData));
    }
  }, [reportData, summary]);

  // Pagination calculations
  const totalPages = Math.ceil(reportData.length / rowsPerPage);
  const paginatedData = reportData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ 
            minHeight: '100vh',
            background: '#f8fafc',
            py: 3
          }}>
            <Container maxWidth="xl">
              {/* Header Section */}
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  mb: 3,
                  borderRadius: 2,
                  background: 'white',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography 
                      variant="h5" 
                      component="h1"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 0.5
                      }}
                    >
                      Exam Reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View and analyze exam performance data
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={fetchExams}
                      disabled={loading}
                      size="small"
                    >
                      {loading ? 'Loading...' : 'Refresh'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={exportToCSV}
                      disabled={selectedExams.length === 0}
                      size="small"
                    >
                      Export CSV
                    </Button>
                  </Stack>
                </Box>

                {/* Exam Selection */}
                <FormControl 
                  fullWidth 
                  sx={{ 
                    mb: 2
                  }}
                  size="small"
                >
                  <InputLabel>Select Exams</InputLabel>
                  <Select
                    multiple
                    value={selectedExams}
                    onChange={(e) => setSelectedExams(e.target.value)}
                    label="Select Exams"
                    disabled={loading || exams.length === 0}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const exam = exams.find(e => e.ex_id === value);
                          return exam ? (
                            <Chip 
                              key={value} 
                              label={exam.ex_title} 
                              size="small"
                              sx={{
                                background: alpha('#1976d2', 0.1),
                                color: 'primary.main',
                                fontWeight: 500
                              }}
                              onDelete={() => {
                                setSelectedExams(selectedExams.filter(id => id !== value));
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                            />
                          ) : null;
                        })}
                      </Box>
                    )}
                  >
                    {loading ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading exams...
                      </MenuItem>
                    ) : exams.length === 0 ? (
                      <MenuItem disabled>No exams available</MenuItem>
                    ) : (
                      exams.map((exam) => (
                        <MenuItem 
                          key={exam.ex_id} 
                          value={exam.ex_id}
                        >
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" fontWeight={500}>{exam.ex_title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Level: {exam.level || 'N/A'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Filters Section */}
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2,
                    borderRadius: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <FilterIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600}>Filters</Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={setStartDate}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small"
                          }
                        }}
                        disabled={reportLoading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={setEndDate}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small"
                          }
                        }}
                        disabled={reportLoading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        label="Min Score %"
                        type="number"
                        value={minScore}
                        onChange={(e) => setMinScore(e.target.value)}
                        InputProps={{ 
                          inputProps: { min: 0, max: 100 },
                          size: "small"
                        }}
                        disabled={reportLoading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        label="Max Score %"
                        type="number"
                        value={maxScore}
                        onChange={(e) => setMaxScore(e.target.value)}
                        InputProps={{ 
                          inputProps: { min: 0, max: 100 },
                          size: "small"
                        }}
                        disabled={reportLoading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                      <TextField
                        fullWidth
                        label="Search User"
                        value={searchUser}
                        onChange={(e) => setSearchUser(e.target.value)}
                        disabled={reportLoading}
                        size="small"
                      />
                    </Grid>
                  </Grid>

                  <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
                    <Button
                      variant="outlined"
                      onClick={clearFilters}
                      disabled={reportLoading}
                      size="small"
                    >
                      Clear Filters
                    </Button>
                    <Button
                      variant="contained"
                      onClick={fetchReport}
                      disabled={selectedExams.length === 0 || reportLoading}
                      startIcon={reportLoading ? <CircularProgress size={16} /> : <ChartIcon />}
                      size="small"
                    >
                      {reportLoading ? 'Generating...' : 'Generate Report'}
                    </Button>
                  </Box>
                </Paper>
              </Paper>

              {/* Summary Cards - Compact and Clean */}
              {summary && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        textAlign: 'center',
                        borderTop: '4px solid',
                        borderColor: 'primary.main',
                        transition: 'box-shadow 0.3s',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                    >
                      <Assessment sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Attempts
                      </Typography>
                      <Typography variant="h4" fontWeight={600} color="primary.main">
                        {summary.total_attempts}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        textAlign: 'center',
                        borderTop: '4px solid',
                        borderColor: 'success.main',
                        transition: 'box-shadow 0.3s',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                    >
                      <TrendingUp sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Average Score
                      </Typography>
                      <Typography variant="h4" fontWeight={600} color="success.main">
                        {summary.avg_score}%
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        textAlign: 'center',
                        borderTop: '4px solid',
                        borderColor: 'info.main',
                        transition: 'box-shadow 0.3s',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Pass Rate
                      </Typography>
                      <Typography variant="h4" fontWeight={600} color="info.main">
                        {summary.pass_rate}%
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        textAlign: 'center',
                        borderTop: '4px solid',
                        borderColor: 'warning.main',
                        transition: 'box-shadow 0.3s',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                    >
                      <People sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Unique Users
                      </Typography>
                      <Typography variant="h4" fontWeight={600} color="warning.main">
                        {summary.total_users}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Report Table */}
              {reportData.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    background: 'white',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            background: alpha('#1976d2', 0.08),
                            '& th': {
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              py: 1.5,
                              borderBottom: '2px solid',
                              borderColor: 'divider'
                            }
                          }}
                        >
                          <TableCell>User</TableCell>
                          <TableCell>Exam</TableCell>
                          <TableCell align="center">Score</TableCell>
                          <TableCell align="center">Correct/Total</TableCell>
                          <TableCell align="center">Time Spent</TableCell>
                          <TableCell>Start Time</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedData.map((row) => (
                          <TableRow 
                            key={row.attempt_id} 
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha('#1976d2', 0.04)
                              }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {row.full_name || `User ${row.user_id}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  @{row.username || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {row.ex_title || 'Unknown Exam'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${row.score_percent || 0}%`}
                                color={getScoreColor(row.score_percent || 0)}
                                size="small"
                                sx={{
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={500}>
                                {row.correct_answers || 0}/{row.total_questions || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {row.time_spent || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {row.start_time ? new Date(row.start_time).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.status || 'unknown'}
                                color={row.status === 'completed' ? 'success' : 'default'}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => viewAttemptDetails(row.attempt_id)}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  <Box 
                    sx={{ 
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        Rows per page:
                      </Typography>
                      <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(e.target.value);
                          setPage(1);
                        }}
                        size="small"
                        sx={{
                          minWidth: 80
                        }}
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                      <Typography variant="body2" color="text.secondary">
                        Showing {((page - 1) * rowsPerPage) + 1} - {Math.min(page * rowsPerPage, reportData.length)} of {reportData.length}
                      </Typography>
                    </Box>
                    
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                      size="small"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Paper>
              )}

              {/* Empty State */}
              {!reportLoading && reportData.length === 0 && selectedExams.length > 0 && (
                <Paper 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    borderRadius: 2,
                    background: 'white',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Assessment sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                    No report data available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filters or select different exams
                  </Typography>
                </Paper>
              )}
            </Container>
          </Box>

          {/* Attempt Details Dialog */}
          <Dialog
            open={detailsDialog}
            onClose={() => setDetailsDialog(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2
              }
            }}
          >
            <DialogTitle
              sx={{
                background: 'primary.main',
                color: 'white',
                fontWeight: 600
              }}
            >
              Attempt Details
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              {selectedAttempt && (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        User
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedAttempt.attempt_info?.full_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Exam
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedAttempt.attempt_info?.ex_title || 'Unknown'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Score
                      </Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight={700}
                        color="primary.main"
                      >
                        {selectedAttempt.summary?.score_percentage || 0}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Correct Answers
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedAttempt.summary?.total_correct || 0}/{selectedAttempt.total_questions || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedAttempt.attempt_info?.time_spent || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {selectedAttempt.answers && selectedAttempt.answers.length > 0 ? (
                    <>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2, mb: 2 }}>
                        Answers
                      </Typography>
                      <TableContainer 
                        component={Paper} 
                        variant="outlined"
                        sx={{ 
                          borderRadius: 1,
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                background: alpha('#1976d2', 0.08),
                                '& th': {
                                  fontWeight: 600
                                }
                              }}
                            >
                              <TableCell>Question</TableCell>
                              <TableCell>Selected Answer</TableCell>
                              <TableCell>Correct Answer</TableCell>
                              <TableCell align="center">Result</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedAttempt.answers.map((answer, index) => (
                              <TableRow 
                                key={index}
                              >
                                <TableCell>
                                  <Typography variant="body2">
                                    {answer.question_text || `Question ${index + 1}`}
                                  </Typography>
                                </TableCell>
                                <TableCell>{answer.selected_option || 'N/A'}</TableCell>
                                <TableCell>{answer.correct_option || 'N/A'}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={answer.is_correct ? 'Correct' : 'Wrong'}
                                    color={answer.is_correct ? 'success' : 'error'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No answer details available
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={() => setDetailsDialog(false)}
                variant="contained"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              onClose={handleSnackbarClose} 
              severity={snackbar.severity}
              sx={{ 
                borderRadius: 1
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default ExamReport;